import type { CreateOrganizationInput } from '@alice-financial/alice-sdk'
import { useCreateOrganization } from '@alice-financial/alice-sdk'
import {
  alpha,
  Box,
  Button,
  Carousel,
  Container,
  FullDialog,
  Link,
  Typography,
  type Theme,
} from '@alice-financial/pretext-ui'
import AddIcon from '@mui/icons-material/Add'
import * as React from 'react'
import { useForm } from 'react-hook-form'
import {
  OrganizationDetailsInputs,
  PayrollSelectionInputs,
  type OrganizationFormFields,
} from './IntakeFormSlides'

interface CreateOrgButtonProps {
  dialog?: boolean
  onSuccess?: (organizationId: number) => void
  onError?: (error: Error) => void
}

export const CreateOrgButton: React.FC<CreateOrgButtonProps> = ({ dialog, onSuccess, onError }) => {
  const [dialogOpen, setDialogOpen] = React.useState(false)

  const handleClick = () => {
    setDialogOpen(true)
  }

  const handleDialogClose = () => {
    setDialogOpen(false)
  }

  const handleDialogSuccess = (organizationId: number) => {
    onSuccess?.(organizationId)
    setDialogOpen(false)
  }

  const handleDialogError = (error: Error) => {
    onError?.(error)
    // Keep dialog open so user can fix issues
  }

  const buttonProps = {
    color: 'primary' as const,
    variant: 'outlined' as const,
    startIcon: <AddIcon />,
    fullWidth: true,
    sx: (theme: Theme) => ({
      mt: 2,
      display: 'flex',
      justifyContent: 'center',
      height: '3rem',
      border: '3px dashed',
      borderColor: alpha(theme.palette.primary.main, 0.3),
    }),
    children: 'Add client',
  }

  return (
    <>
      <Button
        {...buttonProps}
        {...(dialog ? { onClick: handleClick } : { component: Link, to: '/onboard' })}
      />

      {dialog && (
        <IntakeFormDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          onSuccess={handleDialogSuccess}
          onError={handleDialogError}
        />
      )}
    </>
  )
}

// Broker-specific form data type
export type BrokerFormData = OrganizationFormFields

interface BrokerIntakeFormProps {
  onSuccess?: (organizationId: number) => void
  onError?: (error: Error) => void
}

const BrokerIntakeForm: React.FC<BrokerIntakeFormProps> = ({ onSuccess, onError }) => {
  const { mutateAsync: createOrganization } = useCreateOrganization()
  const [activeStep, setActiveStep] = React.useState(0)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setError,
  } = useForm<BrokerFormData>({
    mode: 'onChange',
    defaultValues: {
      organizationName: '',
      legalName: '',
      payrollPlatform: '',
    },
  })

  // Watch form values to determine if current step is valid
  const watchedValues = watch()

  // Function to check if current step is valid
  const isCurrentStepValid = (step: number): boolean => {
    switch (step) {
      case 0: // Organization Details
        return !!(watchedValues.legalName?.trim() && watchedValues.organizationName?.trim())
      case 1: // Payroll Selection
        return !!watchedValues.payrollPlatform?.trim()
      default:
        return true
    }
  }

  const onSubmit = async (data: BrokerFormData) => {
    try {
      const input: CreateOrganizationInput = {
        name: data.organizationName,
        legalName: data.legalName,
        defaultPayrollPlatformCode: data.payrollPlatform,
      }

      const result = await createOrganization({ input })

      if (result.createOrganization?.inputErrors?.length) {
        // Handle validation errors from the server
        result.createOrganization.inputErrors.forEach((error: { argument: string; message: string }) => {
          const fieldName = error.argument as keyof BrokerFormData
          setError(fieldName, { message: error.message })
        })
      } else if (result.createOrganization?.orgId) {
        // Success - organization was created
        const orgId = result.createOrganization.orgId
        console.log('Organization created successfully:', result.createOrganization.orgId)
        onSuccess?.(orgId)
      }
    } catch (error) {
      console.error('Error creating organization:', error)
      const errorObj = error instanceof Error ? error : new Error('Unknown error occurred')
      onError?.(errorObj)
    }
  }

  // Handle step change with validation
  const handleStepChange = (newStep: number) => {
    // If trying to go forward, check if current step is valid
    if (newStep > activeStep && !isCurrentStepValid(activeStep)) {
      // Don't allow advancing if current step is invalid
      return
    }
    setActiveStep(newStep)
  }

  return (
    <Box component="form" onSubmit={handleSubmit(onSubmit)}>
      <Carousel
        onComplete={handleSubmit(onSubmit)}
        onStepChange={handleStepChange}
        disableNext={!isCurrentStepValid(activeStep)}
        mouseOnly
      >
        <OrganizationDetailsInputs<BrokerFormData> register={register} errors={errors} isBrokerView />
        <PayrollSelectionInputs<BrokerFormData> register={register} errors={errors} isBrokerView />
      </Carousel>
    </Box>
  )
}

interface IntakeFormDialogProps {
  open: boolean
  onClose: () => void
  onSuccess?: (organizationId: number) => void
  onError?: (error: Error) => void
}

export const IntakeFormDialog: React.FC<IntakeFormDialogProps> = ({ open, onClose, onSuccess, onError }) => {
  const handleSuccess = (organizationId: number) => {
    onSuccess?.(organizationId)
    onClose()
  }

  const handleError = (error: Error) => {
    onError?.(error)
    // Don't close dialog on error so user can fix issues
  }

  return (
    <FullDialog
      open={open}
      onClose={onClose}
      title={<Typography variant="h2">Add client account</Typography>}
    >
      <Container>
        <BrokerIntakeForm onSuccess={handleSuccess} onError={handleError} />
      </Container>
    </FullDialog>
  )
}
